import React from "react";
import { CloseIcon } from "../../../atoms/icons/close";
import { InfoText } from "./InfoText";
import { AddModalSelectBox } from "../../../atoms/AddModalSelectBox";
import { getMonthOptions } from "../../../util/getMonthOptions";
import { SlideUpModal } from "./SlideUpModal";
import { MoneySlideUpModalInput } from "../../../atoms/MoneySlideUpModalInput";
import { SlideUpDatePicker } from "../../../atoms/SlideUpDatePicker";
import { useFormik } from "formik";
import { getDateFourteenDaysFromNow } from "../../../util/getDateFourteenDaysFromNow";
import { date, number, object } from "yup";
import { SelectButton } from "./SelectButton";
import { isRefinance } from "../../../util/compare_utils";

export const AddModal = ({
  open,
  onClose,
  customFormEdit,
  enteredData,
  content,
}) => {
  const {
    taxes,
    insurance,
    insurancePaymentMonth,
    plannedClosingDate,
    earnestMoney,
    credits,
  } = enteredData;

  const onCustomFormEdit = (name, value) => formik.setFieldValue(name, value);

  const fakeComponent = React.useMemo(
    () => ({ props: { customFormEdit: onCustomFormEdit } }),
    [customFormEdit]
  );

  const minDate = getDateFourteenDaysFromNow();

  const createFieldValidation = () =>
    number()
      .positive("Must be a positive number")
      .integer("Must be an integer")
      .transform((value, originalValue) =>
        /^\d+$/.test(originalValue) ? parseInt(originalValue) : NaN
      )
      .typeError("Must be a positive integer");

  const validationSchema = object().shape({
    plannedClosingDate: date()
      .typeError("Invalid date")
      .required("Date required")
      .min(minDate, "You need at least 15 days for the closing process"),
    taxes: createFieldValidation(),
    insurance: createFieldValidation(),
    earnestMoney: createFieldValidation(),
    credits: createFieldValidation(),
  });

  const formik = useFormik({
    initialValues: {
      taxes: taxes || "",
      insurance: insurance || "",
      insurancePaymentMonth: insurancePaymentMonth || "",
      plannedClosingDate: plannedClosingDate || "",
      earnestMoney: earnestMoney || "",
      credits: credits || "",
    },
    validationSchema,
    onSubmit: (values) => {
      customFormEdit("taxes", values.taxes);
      customFormEdit("insurance", values.insurance);
      customFormEdit("insurancePaymentMonth", values.insurancePaymentMonth);
      customFormEdit("plannedClosingDate", values.plannedClosingDate);
      customFormEdit("earnestMoney", values.earnestMoney);
      customFormEdit("credits", values.credits);
      onClose();
    },
  });

  return (
    <SlideUpModal
      open={open}
      onClose={onClose}
      direction="up"
      in={true}
      mountOnEnter
      unmountOnExit
    >
      <div className="add-modal">
        <div className="left-column add-modal-left-column">
          <form onSubmit={formik.handleSubmit}>
            <div className="add-modal-row">
              <MoneySlideUpModalInput
                name="taxes"
                label="Yearly Estimated Taxes:"
                placeholder="0"
                value={formik.values.taxes}
                onChange={formik.handleChange}
                helperText={formik.errors.taxes}
                error={!!formik.errors.taxes}
              />
              <MoneySlideUpModalInput
                name="insurance"
                label="Yearly Estimated Insurance:"
                placeholder="0"
                value={formik.values.insurance}
                onChange={formik.handleChange}
                helperText={formik.errors.insurance}
                error={!!formik.errors.insurance}
              />
              <AddModalSelectBox
                options={getMonthOptions()}
                name="insurancePaymentMonth"
                label="Insurance Payment Month:"
                id="insurance-payment-month"
                labelId="insurance-payment-month-label"
                value={formik.values.insurancePaymentMonth}
                onChange={formik.handleChange}
              />
            </div>
            <div className="add-modal-row">
              <SlideUpDatePicker
                label="Closing Date:"
                component={fakeComponent}
                name="plannedClosingDate"
                value={formik.values.plannedClosingDate}
                onChange={formik.handleChange}
                helperText={formik.errors.plannedClosingDate}
                error={!!formik.errors.plannedClosingDate}
              />
              {!isRefinance() && (
                <>
                  <MoneySlideUpModalInput
                    placeholder="0"
                    label="Earnest Money Paid:"
                    name="earnestMoney"
                    value={formik.values.earnestMoney}
                    onChange={formik.handleChange}
                    helperText={formik.errors.earnestMoney}
                    error={!!formik.errors.earnestMoney}
                  />
                  <MoneySlideUpModalInput
                    name="credits"
                    label="Seller/Realtor Credit:"
                    placeholder="0"
                    value={formik.values.credits}
                    onChange={formik.handleChange}
                    helperText={formik.errors.credits}
                    error={!!formik.errors.credits}
                  />
                </>
              )}
            </div>
            <div className="add-modal-submit-wrapper">
              <div>
                <SelectButton
                  type="submit"
                  label="Add to comparison"
                  handleClick={formik.handleSubmit}
                  disabled={!formik.isValid}
                />
              </div>
            </div>
          </form>
        </div>

        {content.tip3 && (
          <div className="right-column">
            <InfoText tip={content.tip3} />
          </div>
        )}

        <div className="close-button" onClick={onClose}>
          <CloseIcon />
        </div>
      </div>
    </SlideUpModal>
  );
};
